import { useCallback, useEffect, useState } from "react";
import { Plus, Lightbulb, CalendarDays, BellRing, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, Panel, StatCard, PillTabs, Empty, NativeSelect } from "@/components/crm/common";
import { ContentDetail, PlatformPicker, TYPES, PLATFORMS } from "@/components/crm/marketing/ContentDetail";
import { Performance, totals } from "@/components/crm/marketing/Metrics";
import { BestTime, SmartSlots } from "@/components/crm/marketing/BestTime";
import { DriveConnect } from "@/components/crm/marketing/DriveConnect";

const today = () => new Date().toISOString().slice(0, 10);
const slug = (s) => s.replace(/\W+/g, "-").toLowerCase();
const CHANNELS = [["Instagram", "https://instagram.com/zerodesignstudio/?hl=en"], ["LinkedIn", "https://linkedin.com/company/zero-design-studio"],
  ["Behance", "https://behance.net/siddhesdeshmuk6"], ["YouTube", "https://www.youtube.com/@zerodesignstudio1"], ["Pinterest", "https://in.pinterest.com/Zerodesignstudios/3d-anamorphic-content"]];

function Channels() {
  return (
    <div data-testid="marketing-channels" className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-zinc-400">Our pages:</span>
      {CHANNELS.map(([n, u]) => <a key={n} data-testid={`marketing-channel-${n.toLowerCase()}`} href={u} target="_blank" rel="noreferrer" className="rounded-full border border-zinc-200 bg-white px-3 py-1 font-medium text-zinc-700 transition-colors hover:border-[#A9792C] hover:text-[#A9792C]">{n}</a>)}
    </div>
  );
}

function Card({ t, onOpen }) {
  const late = t.publish_date && t.publish_date < today() && t.stage !== "Published";
  return (
    <button data-testid={`content-card-${t.id}`} onClick={() => onOpen(t)} className="block w-full rounded-lg border border-zinc-100 bg-white p-3 text-left shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-shadow hover:shadow-md">
      <div className="text-[13px] font-semibold text-zinc-900">{t.deliverable}</div>
      <div className="mt-1 flex flex-wrap gap-1">{(t.platforms || []).map((p) => <span key={p} className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] text-zinc-600">{p}</span>)}</div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-zinc-500">
        <span className="truncate">{t.assignee || "Unassigned"}{t.content_type ? ` · ${t.content_type}` : ""}</span>
        {t.publish_date && <span className={`flex shrink-0 items-center gap-1 ${late ? "font-semibold text-rose-600" : ""}`}><CalendarDays className="h-3 w-3" />{t.publish_date}{t.publish_time ? ` ${t.publish_time}` : ""}</span>}
      </div>
      {t.metrics && <div data-testid={`content-card-metrics-${t.id}`} className="mt-1.5 text-[11px] font-medium text-[#A9792C]">{totals(t).views.toLocaleString("en-IN")} views · {totals(t).likes.toLocaleString("en-IN")} likes</div>}
    </button>
  );
}

function Pipeline({ stages, items, onOpen }) {
  return (
    <div data-testid="marketing-pipeline" className="thin-scroll -mx-1 flex gap-3 overflow-x-auto px-1 pb-2">
      {stages.map((s) => {
        const col = items.filter((t) => t.stage === s);
        return (
          <div key={s} data-testid={`marketing-col-${slug(s)}`} className="w-64 shrink-0 rounded-xl bg-zinc-50 p-2">
            <div className="flex items-center justify-between px-1.5 py-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">{s}<span className="rounded-full bg-white px-2 py-0.5 text-zinc-700">{col.length}</span></div>
            <div className="mt-1 space-y-2">{col.map((t) => <Card key={t.id} t={t} onOpen={onOpen} />)}</div>
          </div>
        );
      })}
    </div>
  );
}

function Calendar({ items, onOpen }) {
  const rows = items.filter((t) => t.publish_date && t.stage !== "Published").sort((a, b) => a.publish_date.localeCompare(b.publish_date));
  const days = [...new Set(rows.map((t) => t.publish_date))];
  if (!days.length) return <Panel><Empty text="No upcoming posts. Add a publish date to content to plan the calendar." /></Panel>;
  return (
    <div className="space-y-3" data-testid="marketing-calendar">
      {days.map((d) => (
        <Panel key={d} title={new Date(d).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" }) + (d < today() ? " · overdue" : d === today() ? " · today" : "")} testid={`marketing-day-${d}`}>
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">{rows.filter((t) => t.publish_date === d).map((t) => <Card key={t.id} t={t} onOpen={onOpen} />)}</div>
        </Panel>
      ))}
    </div>
  );
}

function Platforms({ items }) {
  const month = today().slice(0, 7);
  return (
    <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-4" data-testid="marketing-platforms">
      {PLATFORMS.map((p) => {
        const list = items.filter((t) => (t.platforms || []).includes(p));
        return (
          <Panel key={p} title={p} testid={`marketing-platform-${slug(p)}`}>
            <div className="grid grid-cols-3 gap-2 text-center text-xs">
              <div><div className="text-xl font-bold text-zinc-900">{list.filter((t) => t.stage !== "Published").length}</div>In pipeline</div>
              <div><div className="text-xl font-bold text-[#A9792C]">{list.filter((t) => t.stage === "Scheduled").length}</div>Scheduled</div>
              <div><div className="text-xl font-bold text-emerald-600">{list.filter((t) => t.stage === "Published" && (t.published_at || "").startsWith(month)).length}</div>Published this month</div>
            </div>
          </Panel>
        );
      })}
    </div>
  );
}

function NewContent({ open, onOpenChange, members, projects, onCreated, initial, allItems }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ platforms: [], ...initial }); }, [open, initial]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async () => {
    setBusy(true);
    try { await api.post("/marketing/content", f); toast.success("Added to the marketing pipeline"); onCreated(); onOpenChange(false); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const cls = "w-full rounded-lg border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#A9792C]";
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="content-new-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>New content / idea</DialogTitle></DialogHeader>
        <input data-testid="content-new-title" value={f.title || ""} onChange={(e) => set("title")(e.target.value)} placeholder="Title, e.g. Behind the scenes reel — Phoenix launch" className={cls} />
        <PlatformPicker testid="content-new-platform" value={f.platforms || []} onChange={set("platforms")} />
        <SmartSlots testid="content-new-smart-slot" items={allItems || []} platforms={f.platforms || []} onPick={(d, t) => setF((x) => ({ ...x, publish_date: d, publish_time: t }))} />
        <div className="grid grid-cols-2 gap-2">
          <NativeSelect testid="content-new-type" value={f.content_type || ""} onChange={set("content_type")} options={TYPES} placeholder="Content type" />
          <input data-testid="content-new-publish" type="date" value={f.publish_date || ""} onChange={(e) => set("publish_date")(e.target.value)} className={cls} />
          <input data-testid="content-new-publish-time" type="time" value={f.publish_time || ""} onChange={(e) => set("publish_time")(e.target.value)} className={cls} />
          <NativeSelect testid="content-new-assignee" value={f.assignee_id || ""} onChange={set("assignee_id")} options={members.map((m) => ({ value: m.id, label: m.name }))} placeholder="Assign to" />
          <NativeSelect testid="content-new-project" value={f.project_id || ""} onChange={set("project_id")} options={projects.map((p) => ({ value: p.id, label: p.name }))} placeholder="Project (optional)" />
        </div>
        <textarea data-testid="content-new-idea" rows={3} value={f.idea || ""} onChange={(e) => set("idea")(e.target.value)} placeholder="Idea, hook, reference links" className={cls} />
        <Button data-testid="content-new-save" disabled={busy || !(f.title || "").trim()} onClick={submit}>{busy ? "Adding…" : "Add to pipeline"}</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Marketing() {
  const { user, isAdmin } = useAuth();
  const { items: projects } = useCollection("projects");
  const [board, setBoard] = useState(null);
  const [tab, setTab] = useState("pipeline");
  const [open, setOpen] = useState(null);
  const [adding, setAdding] = useState(null);
  const [who, setWho] = useState("");
  const load = useCallback(() => api.get("/marketing/board").then((r) => setBoard(r.data)).catch(() => setBoard({ stages: [], members: [], items: [], denied: true })), []);
  useEffect(() => { load(); const t = setInterval(() => { if (!document.hidden) load(); }, 15000); return () => clearInterval(t); }, [load]);
  if (!board) return <PageHeader num="23" title="Marketing" subtitle="Loading…" />;
  const items = who ? board.items.filter((t) => t.assignee_member_id === who) : board.items;
  const current = open && board.items.find((t) => t.id === open.id);
  const month = today().slice(0, 7);
  const canReview = isAdmin || ["Shubham"].includes(user.name);
  return (
    <>
      <PageHeader num="23" title="Marketing" subtitle="Social media, YouTube, Instagram and LinkedIn content: idea → research → script → shoot/design → edit → HOD approval → scheduled → published. Closing a project auto-creates marketing tasks.">
        {isAdmin && <Button data-testid="marketing-daily-run" variant="outline" onClick={async () => {
          try { const { data } = await api.post("/marketing/daily-reminder/run"); toast.success(`${data.due_today} due today · ${data.overdue} overdue · sent to ${data.sent_to.length ? data.sent_to.join(", ") : "nobody new"} · ${data.results_reminders.length} results reminder(s)`); }
          catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        }}><BellRing className="mr-1 h-4 w-4" />Send today's upload reminder</Button>}
        {isAdmin && <Button data-testid="marketing-weekly-run" variant="outline" onClick={async () => {
          try { const { data } = await api.post("/marketing/weekly-report/run"); toast.success(`Weekly report: ${data.published} published · ${data.missed} missed · ${data.planned} planned${data.note ? ` · ${data.note}` : ""}`); }
          catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        }}><BarChart3 className="mr-1 h-4 w-4" />Send weekly report</Button>}
        <Button data-testid="marketing-add-idea" variant="outline" onClick={() => setAdding({})}><Lightbulb className="mr-1 h-4 w-4" />Quick idea</Button>
        <Button data-testid="marketing-add-content" onClick={() => setAdding({})}><Plus className="mr-1 h-4 w-4" />New content</Button>
      </PageHeader>
      <Channels />
      {isAdmin && <DriveConnect />}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard testid="marketing-stat-ideas" label="Ideas" value={board.items.filter((t) => t.stage === "Idea").length} />
        <StatCard testid="marketing-stat-production" label="In production" value={board.items.filter((t) => ["Research & Data", "Script / Writing", "Design / Shoot", "Editing"].includes(t.stage)).length} tone="text-[#A9792C]" />
        <StatCard testid="marketing-stat-hod" label="Awaiting HOD" value={board.items.filter((t) => t.stage === "HOD Approval").length} tone="text-amber-600" />
        <StatCard testid="marketing-stat-scheduled" label="Scheduled" value={board.items.filter((t) => ["Approved", "Scheduled"].includes(t.stage)).length} />
        <StatCard testid="marketing-stat-published" label="Published this month" value={board.items.filter((t) => t.stage === "Published" && (t.published_at || "").startsWith(month)).length} tone="text-emerald-600" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PillTabs testid="marketing-tab" value={tab} onChange={setTab} tabs={[["pipeline", "Pipeline"], ["calendar", "Content calendar"], ["ideas", "Ideas bank"], ["platforms", "Platforms"], ["performance", "Performance"], ["besttime", "Best time to post"]]} />
        <NativeSelect testid="marketing-member-filter" value={who} onChange={setWho} placeholder={`Whole team (${board.members.map((m) => m.name.split(" ")[0]).join(", ")})`} options={board.members.map((m) => ({ value: m.id, label: m.name }))} />
      </div>
      {tab === "pipeline" && <Pipeline stages={board.stages} items={items} onOpen={setOpen} />}
      {tab === "calendar" && <Calendar items={items} onOpen={setOpen} />}
      {tab === "ideas" && (items.some((t) => t.stage === "Idea") ? <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">{items.filter((t) => t.stage === "Idea").map((t) => <Card key={t.id} t={t} onOpen={setOpen} />)}</div> : <Panel><Empty text="No ideas yet. Click Quick idea to add one." /></Panel>)}
      {tab === "platforms" && <Platforms items={items} />}
      {tab === "performance" && <Performance items={items} onOpen={setOpen} onSynced={load} />}
      {tab === "besttime" && <BestTime items={items} platforms={PLATFORMS} />}
      <NewContent open={!!adding} onOpenChange={(o) => !o && setAdding(null)} members={board.members} projects={projects} onCreated={load} initial={adding} allItems={board.items} />
      <ContentDetail item={current} members={board.members} canReview={canReview} onClose={() => setOpen(null)} reload={load} allItems={board.items} />
    </>
  );
}
